import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Download, FileText, Calendar, CreditCard, Package, Receipt, Upload, X, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtBRL = (v) => new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(Number(v) || 0);
const fmtDate = (s) => { if (!s) return '-'; try { return new Date(s).toLocaleDateString('pt-BR'); } catch { return s; } };

export default function ClienteRomaneioDetalhe() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [r, setR] = useState(null);
  const [loading, setLoading] = useState(true);
  const [comprovantes, setComprovantes] = useState([]);
  const [showUpload, setShowUpload] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== 'cliente') { navigate('/login'); return; }
    carregar();
    carregarComprovantes();
  }, [authLoading, user, id]);

  const carregarComprovantes = async () => {
    try {
      const res = await axios.get(`${API}/cliente/me/romaneios/${id}/comprovantes`);
      setComprovantes(res.data || []);
    } catch { /* silent */ }
  };

  const [uploadBoletoId, setUploadBoletoId] = useState('');

  const enviarComprovante = async () => {
    if (!uploadFile) { toast.error('Selecione um arquivo'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', uploadFile);
      fd.append('descricao', uploadDesc);
      if (uploadBoletoId) fd.append('boleto_id', uploadBoletoId);
      await axios.post(`${API}/cliente/me/romaneios/${id}/comprovante`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Comprovante enviado! Aguarde a confirmação do admin.');
      setShowUpload(false);
      setUploadFile(null);
      setUploadDesc('');
      setUploadBoletoId('');
      carregarComprovantes();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar');
    }
    setUploading(false);
  };

  const carregar = async () => {
    try {
      const res = await axios.get(`${API}/cliente/me/romaneios/${id}`);
      setR(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao carregar romaneio');
      navigate('/cliente/dashboard');
    }
    setLoading(false);
  };

  const downloadPDF = async () => {
    try {
      const token = localStorage.getItem('santa-ana-token');
      const res = await fetch(`${API}/romaneios/${id}/pdf`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `romaneio_${r.numero_pedido}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a); URL.revokeObjectURL(url);
    } catch {
      toast.error('Erro ao baixar PDF');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="w-8 h-8 border-4 border-emerald-600 border-t-transparent rounded-full animate-spin" /></div>;
  if (!r) return null;

  const isPending = r.pendente_validacao_admin;
  const isOrcamento = r.is_orcamento;

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 dark:from-gray-900 dark:to-gray-800">
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/cliente/dashboard')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg" data-testid="cliente-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <div className="flex-1">
            <h1 className="font-bold text-gray-900 dark:text-white">Pedido #{r.numero_pedido}</h1>
            <p className="text-xs text-gray-500">{fmtDate(r.data || r.created_at)}</p>
          </div>
          {!isPending && (
            <button onClick={downloadPDF} className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg flex items-center gap-1.5" data-testid="cliente-download-pdf">
              <Download className="w-3.5 h-3.5" /> PDF
            </button>
          )}
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-5 space-y-4">
        {/* Status banner */}
        <div className={`rounded-xl p-4 border-2 ${
          isPending ? 'border-amber-300 bg-amber-50 dark:bg-amber-900/20'
          : isOrcamento ? 'border-blue-300 bg-blue-50 dark:bg-blue-900/20'
          : 'border-green-300 bg-green-50 dark:bg-green-900/20'
        }`} data-testid="cliente-romaneio-status">
          <p className="text-sm font-bold">
            {isPending ? '⏳ Aguardando confirmação do admin'
              : isOrcamento ? '📋 Orçamento'
              : '✓ Pedido confirmado'}
          </p>
          <p className="text-xs mt-1">
            {isPending
              ? 'Seu pedido foi recebido. O admin vai revisar prazos, descontos e confirmar em breve.'
              : isOrcamento
              ? 'Este é um orçamento. Aguardando sua aprovação ou negociação.'
              : 'Tudo certo! Confira os detalhes, prazos e boletos abaixo.'}
          </p>
        </div>

        {/* Info Resumo */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-2 gap-3 text-sm">
            <Field label="Valor Total" value={fmtBRL(r.valor_total)} bold />
            <Field label="Forma de Pagamento" value={r.forma_pagamento || 'A definir'} />
            <Field label="Vendedor" value={r.vendedor_nome || '-'} />
            <Field label="Origem" value={r.origem === 'portal_cliente' ? 'Portal Cliente' : r.origem || '-'} />
            {r.numero_nota_fiscal && (
              <Field label="Nota Fiscal" value={`Nº ${r.numero_nota_fiscal}`} highlight />
            )}
            {r.nf_pedido_referente && (
              <Field label="Pedido Referente" value={r.nf_pedido_referente} />
            )}
            {(r.desconto_calculado > 0 || r.desconto_valor > 0) && (
              <Field
                label="Desconto Aplicado"
                value={
                  r.desconto_tipo === 'percentual'
                    ? `${r.desconto_valor}% (${fmtBRL(r.desconto_calculado || 0)})`
                    : `${fmtBRL(r.desconto_calculado || r.desconto_valor)}`
                }
                highlight
                colorClass="text-emerald-600"
              />
            )}
          </div>
          {r.observacao && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700">
              <p className="text-xs text-gray-500">Observação:</p>
              <p className="text-sm">{r.observacao}</p>
            </div>
          )}
          {(r.desconto_calculado > 0) && (
            <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 flex items-center gap-2 bg-emerald-50 dark:bg-emerald-900/20 -mx-4 -mb-4 px-4 py-3 rounded-b-xl">
              <span className="text-xl">🎉</span>
              <p className="text-xs font-semibold text-emerald-800 dark:text-emerald-300">
                Você economizou <b>{fmtBRL(r.desconto_calculado)}</b> nesse pedido!
              </p>
            </div>
          )}
        </div>

        {/* Itens */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 flex items-center gap-2">
            <Package className="w-4 h-4 text-emerald-600" />
            <h2 className="font-bold text-sm">Itens do Pedido ({r.items?.length || 0})</h2>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {(r.items || []).map((it, i) => {
              const descItem = it.percentual_desconto || it.desconto_percent || 0;
              return (
                <div key={i} className="px-4 py-3 flex items-center justify-between" data-testid={`romaneio-item-${i}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold truncate">{it.produto_nome}</p>
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="text-xs text-gray-500">{it.quantidade} × {fmtBRL(it.valor_unitario)}</p>
                      {descItem > 0 && (
                        <span className="text-[10px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded-full font-bold" data-testid={`item-desconto-${i}`}>
                          -{descItem}% desconto
                        </span>
                      )}
                    </div>
                  </div>
                  <p className="text-sm font-bold">{fmtBRL(it.valor_total)}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Prazos / Boletos — cards enriquecidos com status */}
        {(() => {
          // Fonte prioritária: r.boletos (com id, valor, status). Fallback: r.parcelas_prazo (só datas).
          let cards = [];
          if (r.boletos && r.boletos.length > 0) {
            cards = r.boletos.map((b, i) => ({
              id: b.id || `boleto-${i}`,
              numero: b.numero || `Parcela ${i + 1}`,
              data_vencimento: b.data_vencimento,
              valor: b.valor,
              status: (b.status || 'pendente').toLowerCase(),
              data_pagamento: b.data_pagamento,
              descricao: b.descricao,
              arquivo_url: b.arquivo_url,
              _from_boletos: true,
            }));
          } else if (Array.isArray(r.parcelas_prazo) && r.parcelas_prazo.length > 0) {
            const totalRestante = (r.valor_total || 0);
            const valorPorParcela = totalRestante / r.parcelas_prazo.length;
            cards = r.parcelas_prazo.map((data, i) => ({
              id: `parcela-${i}`,
              numero: `Parcela ${i + 1}/${r.parcelas_prazo.length}`,
              data_vencimento: data,
              valor: valorPorParcela,
              status: 'pendente',
              _from_boletos: false,
            }));
          }
          if (cards.length === 0) return null;
          const totalCards = cards.length;
          const pagos = cards.filter(c => c.status === 'pago').length;
          const hoje = new Date().toISOString().split('T')[0];
          return (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-testid="cliente-prazos-section">
              <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Receipt className="w-4 h-4 text-purple-600" />
                  <h2 className="font-bold text-sm">Prazos de Pagamento ({totalCards})</h2>
                </div>
                <span className="text-xs font-semibold text-purple-700 dark:text-purple-300">
                  {pagos}/{totalCards} pagos
                </span>
              </div>
              {/* Progresso */}
              <div className="h-1.5 bg-gray-100 dark:bg-gray-700">
                <div
                  className="bg-emerald-500 h-full transition-all duration-500"
                  style={{ width: `${totalCards > 0 ? (pagos / totalCards) * 100 : 0}%` }}
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-3">
                {cards.map((b, i) => {
                  const isLate = b.status !== 'pago' && b.data_vencimento && b.data_vencimento < hoje;
                  const bg = b.status === 'pago'
                    ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-300'
                    : isLate
                      ? 'bg-red-50 dark:bg-red-900/20 border-red-300'
                      : 'bg-gray-50 dark:bg-gray-900/40 border-gray-200 dark:border-gray-700';
                  return (
                    <div key={b.id} className={`rounded-xl p-3 border-2 ${bg}`} data-testid={`prazo-card-${i}`}>
                      <div className="flex items-start justify-between gap-2 mb-2">
                        <div>
                          <p className="text-[10px] uppercase text-gray-500 font-bold tracking-wider">{b.numero}</p>
                          <div className="flex items-center gap-1.5 mt-0.5">
                            <Calendar className="w-3.5 h-3.5 text-gray-400" />
                            <p className="text-sm font-bold">{fmtDate(b.data_vencimento)}</p>
                          </div>
                        </div>
                        {b.status === 'pago' && (
                          <span className="text-[10px] px-2 py-0.5 bg-emerald-500 text-white rounded-full font-black">PAGO</span>
                        )}
                        {isLate && b.status !== 'pago' && (
                          <span className="text-[10px] px-2 py-0.5 bg-red-500 text-white rounded-full font-black">VENCIDO</span>
                        )}
                        {!isLate && b.status !== 'pago' && (
                          <span className="text-[10px] px-2 py-0.5 bg-amber-500 text-white rounded-full font-black">A PAGAR</span>
                        )}
                      </div>
                      <p className="text-2xl font-black text-gray-900 dark:text-white tabular-nums">
                        {fmtBRL(b.valor)}
                      </p>
                      {b.status === 'pago' && b.data_pagamento && (
                        <p className="text-[10px] text-emerald-700 dark:text-emerald-300 mt-1">
                          ✓ Pago em {fmtDate(b.data_pagamento)}
                        </p>
                      )}
                      {b.descricao && (
                        <p className="text-[10px] text-gray-500 mt-1 truncate">{b.descricao}</p>
                      )}
                      <div className="flex items-center gap-1 mt-2">
                        {b.arquivo_url && (
                          <a href={b.arquivo_url} target="_blank" rel="noreferrer" className="flex-1 text-center py-1.5 rounded-lg bg-purple-100 hover:bg-purple-200 text-purple-700 text-[11px] font-bold flex items-center justify-center gap-1" data-testid={`download-boleto-${i}`}>
                            <Download className="w-3 h-3" /> Boleto
                          </a>
                        )}
                        {b.status !== 'pago' && (
                          <button
                            onClick={() => {
                              setUploadBoletoId(b._from_boletos ? b.id : '');
                              setUploadDesc(`Pagamento ${b.numero} - ${fmtDate(b.data_vencimento)}`);
                              setShowUpload(true);
                            }}
                            className="flex-1 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-bold flex items-center justify-center gap-1"
                            data-testid={`enviar-comp-prazo-${i}`}
                          >
                            <Upload className="w-3 h-3" /> Enviar Comprovante
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })()}

        {/* Vencimento */}
        {r.data_vencimento && !r.boletos?.length && (
          <div className="bg-white dark:bg-gray-800 rounded-xl p-4 shadow-sm border border-gray-200 dark:border-gray-700 flex items-center gap-3">
            <CreditCard className="w-5 h-5 text-amber-600" />
            <div>
              <p className="text-xs text-gray-500">Vencimento</p>
              <p className="text-sm font-bold">{fmtDate(r.data_vencimento)}</p>
            </div>
          </div>
        )}

        {/* MEUS COMPROVANTES (cliente envia) */}
        {!isPending && (
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden" data-testid="cliente-comprovantes-section">
            <div className="px-4 py-2.5 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Upload className="w-4 h-4 text-emerald-600" />
                <h2 className="font-bold text-sm">Comprovantes de Pagamento</h2>
              </div>
              <button onClick={() => setShowUpload(true)} className="px-2.5 py-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold rounded-lg flex items-center gap-1" data-testid="btn-enviar-comprovante-cliente">
                <Upload className="w-3 h-3" /> Enviar
              </button>
            </div>
            {comprovantes.length === 0 ? (
              <p className="px-4 py-4 text-xs text-gray-500 text-center">Nenhum comprovante enviado ainda.</p>
            ) : (
              <div className="divide-y divide-gray-200 dark:divide-gray-700">
                {comprovantes.map((c, i) => (
                  <div key={c.id} className="px-4 py-2.5 flex items-center justify-between" data-testid={`comprovante-cliente-${i}`}>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-mono truncate">{c.filename}</p>
                      <p className="text-[10px] text-gray-500">{fmtDate(c.created_at)} · {c.status === 'pendente_revisao' ? '⏳ aguardando confirmação' : '✓ confirmado'}</p>
                      {c.descricao && <p className="text-[10px] italic">{c.descricao}</p>}
                    </div>
                    <a href={c.url} target="_blank" rel="noreferrer" className="p-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg">
                      <Download className="w-3.5 h-3.5" />
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Enviar Comprovante */}
      {showUpload && (
        <div className="fixed inset-0 z-30 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowUpload(false)}>
          <div onClick={(e) => e.stopPropagation()} className="bg-white dark:bg-gray-800 rounded-2xl w-full max-w-md p-5 space-y-3" data-testid="upload-comprovante-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-bold">Enviar Comprovante de Pagamento</h2>
              <button onClick={() => setShowUpload(false)} className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
                <X className="w-4 h-4" />
              </button>
            </div>
            <label className="block border-2 border-dashed border-emerald-300 rounded-lg p-6 text-center cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-900/10">
              <Upload className="w-8 h-8 mx-auto text-emerald-600 mb-1" />
              <p className="text-sm font-semibold">{uploadFile ? uploadFile.name : 'Clique para selecionar arquivo'}</p>
              <p className="text-[10px] text-gray-500">PDF, JPG, PNG até 10MB</p>
              <input type="file" accept="image/*,application/pdf" hidden onChange={(e) => setUploadFile(e.target.files?.[0] || null)} data-testid="upload-comprovante-input" />
            </label>
            <input type="text" placeholder="Descrição (opcional, ex: PIX 28/06)" value={uploadDesc} onChange={(e) => setUploadDesc(e.target.value)}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 outline-none focus:ring-2 focus:ring-emerald-500"
              data-testid="upload-comprovante-desc" />
            <button onClick={enviarComprovante} disabled={!uploadFile || uploading}
              className="w-full bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-400 text-white py-2.5 rounded-lg font-bold flex items-center justify-center gap-2"
              data-testid="upload-comprovante-submit">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Enviando...' : 'Enviar'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value, bold, highlight, colorClass }) => (
  <div className={highlight ? 'bg-emerald-50 dark:bg-emerald-900/20 rounded-lg px-2 py-1 -mx-1' : ''}>
    <p className="text-xs text-gray-500">{label}</p>
    <p className={`text-sm ${bold ? 'font-bold text-emerald-700' : ''} ${colorClass || ''}`}>{value}</p>
  </div>
);
